/**
 * Consent Management page – Phase 3B.
 *
 * Lets patients:
 *   Tab 1 – Pending Requests : review and approve / deny incoming access requests.
 *   Tab 2 – Active Consents  : see who has access and revoke any grant.
 *   Tab 3 – Consent History  : browse the full immutable consent chain with
 *                              hash visualisation and chain-integrity badge.
 *
 * The page degrades gracefully: all API calls fall back to mock data when
 * the backend is unavailable, so the UI is always usable in development.
 *
 * Design system: TailwindCSS + Radix UI (via shadcn/ui components) + Lucide icons.
 * Brand colour: KaiMed teal (#3D7A74) surfaced via the Tailwind token
 * ``kaimed-sage`` / ``kaimed-sage-dark`` defined in tailwind.config.
 */

import React, { useState } from 'react';
import { Shield, CheckCircle, XCircle, RotateCcw, Clock, AlertTriangle, Hash, Link2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '../hooks/use-auth';

import {
  useConsentRequests,
  useConsentHistory,
  useActiveConsents,
  useConsentVerification,
  useGrantConsent,
  useDenyConsent,
  useRevokeConsent,
} from '../hooks/use-consent';

import type { ConsentEvent, ConsentRequest } from '../types/consent-types';

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Shorten a hex hash for display: first `chars` characters + last 4. */
export function truncateHash(hash: string, chars = 12): string {
  if (!hash) return '—';
  return `${hash.slice(0, chars)}…${hash.slice(-4)}`;
}

function daysUntil(iso: string | null): string {
  if (!iso) return 'Never expires';
  const diff = Math.ceil(
    (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  );
  if (diff <= 0) return 'Expired';
  if (diff === 1) return 'Expires tomorrow';
  return `Expires in ${diff} days`;
}

// ---------------------------------------------------------------------------
// Shared badge components
// ---------------------------------------------------------------------------

const ActionBadge: React.FC<{ action: ConsentEvent['action'] }> = ({ action }) => {
  const map: Record<ConsentEvent['action'], { label: string; className: string }> = {
    request: { label: 'Request', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
    grant: { label: 'Granted', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
    deny: { label: 'Denied', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
    revoke: { label: 'Revoked', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  };
  const cfg = map[action] ?? map.request;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', cfg.className)}>
      {cfg.label}
    </span>
  );
};

const ActionIcon: React.FC<{ action: ConsentEvent['action']; className?: string }> = ({
  action,
  className,
}) => {
  const cls = cn('h-5 w-5 flex-shrink-0', className);
  switch (action) {
    case 'grant':
      return <CheckCircle className={cn(cls, 'text-emerald-500')} />;
    case 'deny':
      return <XCircle className={cn(cls, 'text-red-500')} />;
    case 'revoke':
      return <RotateCcw className={cn(cls, 'text-amber-500')} />;
    default:
      return <Clock className={cn(cls, 'text-blue-500')} />;
  }
};

// ---------------------------------------------------------------------------
// Tab 1: Pending Requests
// ---------------------------------------------------------------------------

const PendingRequestCard: React.FC<{
  request: ConsentRequest;
  patientId: string;
}> = ({ request, patientId }) => {
  const { toast } = useToast();
  const grantMutation = useGrantConsent(patientId);
  const denyMutation = useDenyConsent(patientId);

  const handleGrant = () => {
    grantMutation.mutate(
      {
        patientId,
        requestEventId: Number(request.id),
        providerId: 0, // resolved server-side from the request event
        providerName: request.providerName,
        providerOrganization: request.providerOrganization,
        scope: { recordTypes: request.requestedRecordTypes },
        purpose: request.purpose,
        expiresInDays:
          request.requestedDuration.includes('year')
            ? 365
            : request.requestedDuration.includes('day')
              ? parseInt(request.requestedDuration, 10)
              : undefined,
      },
      {
        onSuccess: () =>
          toast({
            title: 'Access granted',
            description: `${request.providerName} can now access your records.`,
          }),
        onError: () =>
          toast({ title: 'Something went wrong', variant: 'destructive' }),
      },
    );
  };

  const handleDeny = () => {
    denyMutation.mutate(
      {
        patientId,
        requestEventId: Number(request.id),
        providerId: 0,
        providerName: request.providerName,
        providerOrganization: request.providerOrganization,
        scope: { recordTypes: request.requestedRecordTypes },
        purpose: request.purpose,
      },
      {
        onSuccess: () =>
          toast({
            title: 'Access denied',
            description: `${request.providerName}'s request was declined.`,
          }),
        onError: () =>
          toast({ title: 'Something went wrong', variant: 'destructive' }),
      },
    );
  };

  const isBusy = grantMutation.isPending || denyMutation.isPending;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base font-semibold text-foreground truncate">
              {request.providerName}
            </CardTitle>
            <CardDescription className="mt-0.5 text-sm text-muted-foreground">
              {request.providerOrganization}
            </CardDescription>
          </div>
          <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            <Clock className="h-3 w-3" /> Pending
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pb-4">
        {/* Purpose */}
        <p className="text-sm text-foreground">
          <span className="font-medium">Purpose: </span>
          {request.purpose}
        </p>

        {/* Duration */}
        <p className="text-sm text-foreground">
          <span className="font-medium">Duration requested: </span>
          {request.requestedDuration}
        </p>

        {/* Record types */}
        <div>
          <p className="text-sm font-medium text-foreground mb-1.5">Records requested:</p>
          <div className="flex flex-wrap gap-1.5">
            {request.requestedRecordTypes.map((rt) => (
              <span
                key={rt}
                className="inline-flex items-center rounded-md bg-[#3D7A74]/10 px-2 py-0.5 text-xs font-medium text-[#3D7A74] dark:bg-[#3D7A74]/20 dark:text-emerald-300"
              >
                {rt.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground">
          Requested {formatDate(request.requestedAt)}
        </p>
      </CardContent>

      <CardFooter className="flex gap-2 pt-0">
        <Button
          size="sm"
          className="flex-1 bg-[#3D7A74] hover:bg-[#2d6056] text-white"
          disabled={isBusy}
          onClick={handleGrant}
        >
          <CheckCircle className="mr-1.5 h-4 w-4" />
          {grantMutation.isPending ? 'Granting…' : 'Approve'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="flex-1 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950"
          disabled={isBusy}
          onClick={handleDeny}
        >
          <XCircle className="mr-1.5 h-4 w-4" />
          {denyMutation.isPending ? 'Declining…' : 'Decline'}
        </Button>
      </CardFooter>
    </Card>
  );
};

const PendingRequestsTab: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data: requests = [], isLoading, isError } = useConsentRequests(patientId);

  if (isLoading) return <TabSkeleton count={2} />;
  if (isError)
    return (
      <EmptyState
        icon={<AlertTriangle className="h-8 w-8 text-amber-500" />}
        title="Could not load requests"
        description="Showing mock data — backend may be unavailable."
      />
    );
  if (requests.length === 0)
    return (
      <EmptyState
        icon={<CheckCircle className="h-8 w-8 text-emerald-500" />}
        title="No pending requests"
        description="You have no access requests waiting for a response."
      />
    );

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {requests.map((req) => (
        <PendingRequestCard key={req.id} request={req} patientId={patientId} />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 2: Active Consents
// ---------------------------------------------------------------------------

const ActiveConsentRow: React.FC<{
  event: ConsentEvent;
  patientId: string;
}> = ({ event, patientId }) => {
  const { toast } = useToast();
  const revokeMutation = useRevokeConsent(patientId);

  const handleRevoke = () => {
    revokeMutation.mutate(
      { eventId: event.id, payload: { patientId } },
      {
        onSuccess: () =>
          toast({
            title: 'Consent revoked',
            description: `${event.providerName}'s access has been removed.`,
          }),
        onError: () =>
          toast({ title: 'Something went wrong', variant: 'destructive' }),
      },
    );
  };

  return (
    <Card>
      <CardContent className="flex flex-col sm:flex-row sm:items-center gap-4 py-4">
        {/* Provider info */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground truncate">{event.providerName}</p>
          <p className="text-sm text-muted-foreground truncate">
            {event.providerOrganization}
          </p>
        </div>

        {/* Scope chips */}
        <div className="flex flex-wrap gap-1.5 flex-1">
          {event.scope.recordTypes.map((rt) => (
            <span
              key={rt}
              className="inline-flex items-center rounded-md bg-[#3D7A74]/10 px-2 py-0.5 text-xs font-medium text-[#3D7A74] dark:bg-[#3D7A74]/20 dark:text-emerald-300"
            >
              {rt.replace(/_/g, ' ')}
            </span>
          ))}
          {event.scope.recordTypes.length === 0 && (
            <span className="text-xs text-muted-foreground italic">All records</span>
          )}
        </div>

        {/* Expiry */}
        <p
          className={cn(
            'text-xs font-medium whitespace-nowrap',
            event.expiresAt && new Date(event.expiresAt).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-muted-foreground',
          )}
        >
          {daysUntil(event.expiresAt)}
        </p>

        {/* Revoke */}
        <Button
          size="sm"
          variant="outline"
          className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950 flex-shrink-0"
          disabled={revokeMutation.isPending}
          onClick={handleRevoke}
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          {revokeMutation.isPending ? 'Revoking…' : 'Revoke'}
        </Button>
      </CardContent>
    </Card>
  );
};

const ActiveConsentsTab: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data: consents = [], isLoading, isError } = useActiveConsents(patientId);

  if (isLoading) return <TabSkeleton count={3} />;
  if (isError)
    return (
      <EmptyState
        icon={<AlertTriangle className="h-8 w-8 text-amber-500" />}
        title="Could not load consents"
        description="Showing mock data — backend may be unavailable."
      />
    );
  if (consents.length === 0)
    return (
      <EmptyState
        icon={<Shield className="h-8 w-8 text-[#3D7A74]" />}
        title="No active consents"
        description="No providers currently have access to your records."
      />
    );

  return (
    <div className="space-y-3">
      {consents.map((c) => (
        <ActiveConsentRow key={c.id} event={c} patientId={patientId} />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 3: Consent History – chain visualisation
// ---------------------------------------------------------------------------

const ChainIntegrityBadge: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data, isLoading, refetch, isFetching } = useConsentVerification(patientId);

  return (
    <div className="flex items-center gap-2">
      {isLoading ? (
        <span className="text-xs text-muted-foreground">Checking chain…</span>
      ) : data?.isValid ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
          <CheckCircle className="h-3.5 w-3.5" />
          Chain Verified ({data.eventCount} events)
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-300">
          <AlertTriangle className="h-3.5 w-3.5" />
          Chain Integrity Issue ({data?.violations.length ?? 0} violation
          {data && data.violations.length !== 1 ? 's' : ''})
        </span>
      )}
      <button
        onClick={() => refetch()}
        disabled={isFetching}
        className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
        title="Re-verify chain"
      >
        <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
      </button>
    </div>
  );
};

const HistoryEventNode: React.FC<{
  event: ConsentEvent;
  isFirst: boolean;
  isLast: boolean;
}> = ({ event, isFirst, isLast }) => {
  const [showHash, setShowHash] = useState(false);

  return (
    <div className="relative flex gap-4">
      {/* Vertical chain line */}
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ring-2',
            event.action === 'grant' && 'bg-emerald-50 ring-emerald-200 dark:bg-emerald-900/30 dark:ring-emerald-700',
            event.action === 'deny' && 'bg-red-50 ring-red-200 dark:bg-red-900/30 dark:ring-red-700',
            event.action === 'revoke' && 'bg-amber-50 ring-amber-200 dark:bg-amber-900/30 dark:ring-amber-700',
            event.action === 'request' && 'bg-blue-50 ring-blue-200 dark:bg-blue-900/30 dark:ring-blue-700',
          )}
        >
          <ActionIcon action={event.action} className="h-4 w-4" />
        </div>
        {/* Chain link line below the node */}
        {!isLast && (
          <div className="mt-1 flex-1 w-px border-l-2 border-dashed border-border min-h-[2rem]" />
        )}
      </div>

      {/* Event card */}
      <div className="flex-1 pb-6">
        <Card>
          <CardContent className="p-4 space-y-2.5">
            {/* Header row */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <ActionBadge action={event.action} />
                <span className="font-semibold text-sm text-foreground truncate">
                  {event.providerName}
                </span>
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {formatDate(event.createdAt)}
              </span>
            </div>

            {/* Purpose & org */}
            <p className="text-sm text-muted-foreground">
              {event.providerOrganization} — {event.purpose}
            </p>

            {/* Scope chips */}
            {event.scope.recordTypes.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {event.scope.recordTypes.map((rt) => (
                  <span
                    key={rt}
                    className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                  >
                    {rt.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}

            {/* Hash chain info */}
            <button
              onClick={() => setShowHash((p) => !p)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Hash className="h-3 w-3" />
              {showHash ? 'Hide hash' : 'Show hash'}
            </button>

            {showHash && (
              <div className="rounded-md bg-muted/60 p-3 space-y-1.5 font-mono text-xs break-all">
                <div className="flex items-start gap-2">
                  <Hash className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-[#3D7A74]" />
                  <div>
                    <span className="text-muted-foreground">This event: </span>
                    <span className="text-foreground">{event.eventHash}</span>
                  </div>
                </div>
                {event.prevHash && (
                  <div className="flex items-start gap-2">
                    <Link2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground">Prev event: </span>
                      <span className="text-foreground">{event.prevHash}</span>
                    </div>
                  </div>
                )}
                {isFirst && (
                  <p className="text-muted-foreground italic">
                    Genesis block — no previous hash.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ConsentHistoryTab: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data: events = [], isLoading, isError } = useConsentHistory(patientId);

  return (
    <div className="space-y-4">
      {/* Chain integrity banner */}
      <div className="flex items-center justify-between flex-wrap gap-3 rounded-2xl border border-[var(--border-subtle)] bg-white/70 dark:bg-slate-800/60 backdrop-blur-md px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-[#3D7A74]" />
          <span className="text-sm font-medium text-foreground">
            Blockchain-Anchored Audit Trail
          </span>
        </div>
        <ChainIntegrityBadge patientId={patientId} />
      </div>

      {/* Events timeline */}
      {isLoading && <TabSkeleton count={4} />}
      {isError && (
        <EmptyState
          icon={<AlertTriangle className="h-8 w-8 text-amber-500" />}
          title="Could not load history"
          description="Showing mock data — backend may be unavailable."
        />
      )}
      {!isLoading && !isError && events.length === 0 && (
        <EmptyState
          icon={<Hash className="h-8 w-8 text-muted-foreground" />}
          title="No consent events yet"
          description="Your consent history will appear here once you start granting or denying access."
        />
      )}
      {!isLoading && !isError && events.length > 0 && (
        <div className="pt-2">
          {events.map((event, idx) => (
            <HistoryEventNode
              key={event.id}
              event={event}
              isFirst={idx === 0}
              isLast={idx === events.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared loading / empty state components
// ---------------------------------------------------------------------------

const TabSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="h-28 w-full animate-pulse rounded-xl bg-muted"
      />
    ))}
  </div>
);

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div className="flex flex-col items-center gap-3 py-16 text-center">
    {icon}
    <p className="font-semibold text-foreground">{title}</p>
    <p className="text-sm text-muted-foreground max-w-xs">{description}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

const ConsentManagement: React.FC = () => {
  const { user } = useAuth();
  const patientId = user?.id ?? null;
  const { data: pendingRequests } = useConsentRequests(patientId);
  const pendingCount = pendingRequests?.length ?? 0;

  if (!user?.id) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <EmptyState
          icon={<AlertTriangle className="h-8 w-8 text-amber-500" />}
          title="Not authenticated"
          description="Please log in to manage your consent settings."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 space-y-6">
      {/* Page header */}
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#3D7A74]/10">
          <Shield className="h-6 w-6 text-[#3D7A74]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Consent Management
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Control who can access your medical records. Every decision is
            cryptographically signed and stored in an immutable audit trail.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="relative">
            Pending Requests
            {pendingCount > 0 && (
              <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#3D7A74] text-[10px] font-bold text-white">
                {pendingCount > 9 ? '9+' : pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="active">Active Consents</TabsTrigger>
          <TabsTrigger value="history">Consent History</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <PendingRequestsTab patientId={patientId!} />
        </TabsContent>

        <TabsContent value="active" className="mt-6">
          <ActiveConsentsTab patientId={patientId!} />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <ConsentHistoryTab patientId={patientId!} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConsentManagement;
